<template>
  <div class="horizontal-license-login">
    <error-message
      :error="authError"
      title="Fehler beim Anmelden"
      :allow-retry="false"
      :message-by-status="{
        401: loginTypeForStatus === 'startcode' ? 'Der angegebene Startcode ist ungültig. Bitte überprüfen Sie Ihre Angabe!' : 'Der angegebene Lizenznehmer-Name ist nicht korrekt. Bitte vergleichen Sie Ihre Angabe mit dem Lizenzdokument. Die exakte Schreibweise ist wichtig.',
        423: 'Diese Lizenz ist bereits gebunden. Bitte verwenden Sie anstatt des Startcodes den Namen des Lizenznehmers.',
      }"
    />
    <error-message
      :error="loadStatusError"
      title="Fehler beim Anmelden mit Lizenzschlüssel"
      :allow-retry="false"
      :message-by-status="{
        404: 'Bitte überprüfen Sie Ihren Lizenzschlüssel',
        403: 'Bei dieser Lizenz kann sich nicht angemeldet werden, da sie deaktiviert wurde. Wahrscheinlich wurde bereits ein Update auf eine neuere METACOM Version für diese Lizenz durchgeführt. Sie haben in diesem Fall Zugangsdaten zu einer neuen Lizenz mit einem abweichenden Lizenzschlüssel erhalten.',
      }"
    />
    <b-field
      label="Mit Lizenz anmelden"
    >
      <b-field grouped>
        <license-key-input
          v-model="key"
          :disabled="isLoadingStatus"
          @enter="retrieveLicenseStatus"
        />
        <license-authentication-code-input
          v-if="hasValidKey && hasValidStatus"
          v-model="authenticationCode"
          focus
          :login-type="loginTypeForStatus"
          :disabled="isLoadingAuth"
          @enter="onSubmitButtonPressed"
        />
        <b-field>
          <b-button
            type="is-primary"
            icon-left="arrow-right"
            :loading="isLoadingStatus || isLoadingAuth"
            :disabled="!hasValidKey || hasValidStatus && !hasAuthenticationCode"
            @click="onSubmitButtonPressed"
          >
            {{ currentStep === 'key' ? 'Weiter' : 'Anmelden' }}
          </b-button>
        </b-field>
      </b-field>
    </b-field>
  </div>
</template>
<script>
import LicenseKeyInput from '../LicenseKeyInput.vue'
import LicenseAuthenticationCodeInput from '../LicenseAuthenticationCodeInput.vue'
import AuthService from '@/services/api/Auth.js'
import LicenseService from '@/services/api/License.js'
import ErrorMessage from '@/components/general/ErrorMessage.vue'

export default {
  components: {
    LicenseKeyInput,
    LicenseAuthenticationCodeInput,
    ErrorMessage
  },
  props: {
    allowExistingUpgradeSession: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      key: null,
      authenticationCode: null,
      status: null,

      isLoadingStatus: false,
      loadStatusError: null,
      isLoadingAuth: false,
      authError: null,

      license: null,
      authToken: null
    }
  },
  computed: {
    strippedKey () {
      const strippedKey = this.key?.replace(/[^A-Za-z0-9]/g, '')
      return strippedKey
    },
    strippedAuthenticationCode () {
      if (this.loginTypeForStatus === 'holder') {
        return this.authenticationCode
      } else {
        return this.authenticationCode.replace(' ', '')
      }
    },
    hasValidKey () {
      return this.strippedKey != null && this.strippedKey.length === 12
    },
    hasValidStatus () {
      return this.status === 'unbound' || this.status === 'bound'
    },
    loginTypeForStatus () {
      switch (this.status) {
        case 'unbound': return 'startcode'
        case 'bound': return 'holder'
        default: return null
      }
    },
    hasAuthenticationCode () {
      return this.authenticationCode?.length > 0 ?? false
    },
    currentStep () {
      if (this.hasValidStatus) {
        return 'authenticationCode'
      }

      return 'key'
    }
  },
  watch: {
    key () {
      this.reset()
    }
  },
  /**
   * Add direct login url support
   */
  created () {
  },
  methods: {
    reset () {
      this.status = null
      this.authenticationCode = null
    },
    async onSubmitButtonPressed () {
      if (this.status == null && this.hasValidKey) {
        return this.retrieveLicenseStatus()
      } else if (this.hasValidStatus && this.hasAuthenticationCode) {
        return this.login()
      }
    },
    async retrieveLicenseStatus () {
      if (!this.hasValidKey) {
        return
      }

      this.isLoadingStatus = true
      this.loadStatusError = null
      try {
        const response = await AuthService.getLicenseStatusByKey(this.strippedKey)
        this.status = response.data.data.status

        this.isLoadingStatus = false
      } catch (error) {
        this.isLoadingStatus = false
        this.loadStatusError = error
        console.error(error)
      }
    },
    async login () {
      if (!this.hasValidKey || !this.hasValidStatus || !this.hasAuthenticationCode) {
        return
      }

      this.isLoadingAuth = true
      this.authError = null
      try {
        const response = await AuthService.authenticateUserOfType('license', {
          key: this.strippedKey,
          authenticationCode: this.strippedAuthenticationCode
        })

        this.authToken = response.data.data.token
        this.license = response.data.data.userData

        // Check if it is allowed to continue with existing upgrade session
        if (this.allowExistingUpgradeSession) {
          this.$emit('authenticated', { license: this.license, authToken: this.authToken })
          this.isLoadingAuth = false
          return
        }

        const upgradeOrExtensionResponse = await LicenseService.getLicenseUpgradeOrExtensionStatusByLicenseId(this.license._id, this.authToken)
        const upgradeSessionPurchaseType = upgradeOrExtensionResponse.data.purchaseType
        const upgradeSessionStatus = upgradeOrExtensionResponse.data.status

        if (!['offered', 'purchased', 'completed'].includes(upgradeSessionStatus)) {
          this.$emit('authenticated', { license: this.license, authToken: this.authToken })
        } else {
          this.showDialog(upgradeSessionStatus, upgradeSessionPurchaseType)
        }
        this.isLoadingAuth = false
      } catch (error) {
        this.isLoadingAuth = false
        this.authError = error
        console.error(error)
      }
    },
    resetForm () {
      this.key = null
      this.authenticationCode = null
      this.status = null
      this.license = null
      this.authToken = null
      this.authError = null
      this.loadStatusError = null
    },
    showDialog (upgradeSessionStatus, upgradeSessionPurchaseType) {
      if (upgradeSessionStatus === 'offered') {
        this.$buefy.dialog.confirm({
          title: 'Bestehendes Angebot',
          message: 'Es existiert bereits ein Angebot, in welchem diese Lizenz berücksichtigt wurde. <br> <h2 style="font-weight: bold;">Angebot wahrnehmen</h2> Der Angebotscode kann unter „METACOM 9 Desktop kaufen -> Bestehendes Angebot (Angebotscode eingeben)” eingegeben werden, um selbiges wahrzunehmen. <br> <h2 style="font-weight: bold;">Angebot verwerfen</h2>Durch das Fortfahren mit dieser Bestellung wird das Angebot verworfen und kann nicht mehr bestellt werden. ',
          confirmText: 'Bestehendes Angebot verwerfen',
          cancelText: 'Abbrechen',
          onConfirm: () => {
            this.$emit('authenticated', { license: this.license, authToken: this.authToken })
          },
          onCancel: () => this.resetForm()
        })
        return
      }
      let message = ''
      if (upgradeSessionStatus === 'purchased') {
        message = `Für diese Lizenz wurde bereits ${upgradeSessionPurchaseType === 'extension' ? 'eine Erweiterung' : 'ein Update'} bestellt. Die Bestellung dazu befindet sich aktuell noch in Bearbeitung.${upgradeSessionPurchaseType === 'extension' ? ' Bitte warten Sie, bis die laufende Erweiterungsbestellung abgeschlossen ist. Sobald die Bestellung abgeschlossen ist, können Sie eine weitere Erweiterung bestellen. Wir empfehlen Ihnen Erweiterungen zu bündeln, da Sie so von unserem Mengenrabatt profitieren.' : ' Sobald die Bestellung abgeschlossen ist, können Sie sich mit den Zugangsdaten der neuen Lizenz anmelden.'}`
      } else if (upgradeSessionStatus === 'completed') {
        message = 'Für diese Lizenz wurde bereits ein Update gekauft. Mit dieser Lizenz können Sie sich nicht mehr anmelden.'
      }
      this.$buefy.dialog.alert({
        title: `Lizenz${upgradeSessionPurchaseType === 'extension' ? 'erweiterung' : 'update'} nicht möglich`,
        message,
        confirmText: 'Mit anderer Lizenz anmelden',
        type: 'is-info',
        hasIcon: true,
        onConfirm: () => this.resetForm()
      })
    }
  }
}
</script>
<style lang="scss" scoped>
@import '@/assets/scss/bulma-variables.scss';
form {
  padding-top: 40px;
  padding-bottom: 40px;

  .field {
    margin-bottom: 0;
  }

  button.submit {
    margin-top: 1em;
  }
  p.info {
    padding-top: 40px;
    padding-bottom: 20px;
  }
}
.hero {
  background: $primary;
}
.help-item {
  width: 350px;
  clear:both;
  overflow: hidden;
  p {
    padding-top: 10px;
  }

  img {
    width: 180px;
    float: left;
  }
}
.license-help-links {
  margin-top: 1em;
}
</style>
